import { createContext, useCallback, useContext, useMemo, useState } from "react";

/**
 * BillingContext — Phase 8.
 *
 * Owns the global "Upgrade required" modal so any component (an API
 * interceptor, a button click handler, etc.) can surface the upgrade prompt
 * without prop-drilling. Keys map 1:1 to the backend's 402 `error` strings:
 *
 *   - voice_not_available_in_free → require_tier: professional
 *   - history_not_available_in_free → require_tier: professional
 *   - routines_limit               → require_tier: professional
 *   - siri_not_available           → require_tier: elite
 *   - insufficient_tokens          → require_tier: executive (recharge nudge)
 *
 * The interceptor in `lib/api.js` calls `triggerUpgrade(detail)` whenever a
 * 402 surfaces; UpgradeModal reads it and renders the right copy.
 */
const BillingContext = createContext(null);

export function BillingProvider({ children }) {
  const [upgradePrompt, setUpgradePrompt] = useState(null);

  const triggerUpgrade = useCallback((detail) => {
    if (!detail) return;
    setUpgradePrompt({
      error: detail.error,
      requireTier: detail.require_tier || "professional",
      message: detail.message,
      balance: detail.balance,
      ts: Date.now(),
    });
  }, []);

  const dismissUpgrade = useCallback(() => setUpgradePrompt(null), []);

  const value = useMemo(
    () => ({ upgradePrompt, triggerUpgrade, dismissUpgrade }),
    [upgradePrompt, triggerUpgrade, dismissUpgrade],
  );

  return (
    <BillingContext.Provider value={value}>{children}</BillingContext.Provider>
  );
}

export function useBilling() {
  const ctx = useContext(BillingContext);
  if (!ctx) throw new Error("useBilling must be used inside <BillingProvider>");
  return ctx;
}
